import { FC } from 'react'
import { Copy, User } from 'lucide-react'
import { useIntl } from 'react-intl'
import { toast } from 'sonner'
import { ChatMessage } from '../../../app/modules/types/chatBot'
import AssistantRow from './AssistantRow'
import AnswerBubble from './AnswerBubble'
import TurnActivity from './TurnActivity'
import { useTurnTrace } from './chatTraceStore'
import { isEmptyTrace } from './turnActivityState'

type Props = { message: ChatMessage }

const MessageBubble: FC<Props> = ({ message }) => {
  const intl = useIntl()
  /**
   * Keyed off the message's own `chat_id`, so nothing has to be drilled down
   * from `ChatInner`. Session-scoped: after a reload there is no trace and this
   * is simply undefined.
   */
  const trace = useTurnTrace(message.chat_id, message.id)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message.content)
      toast.info(intl.formatMessage({ id: 'Copied' }))
    } catch {
      toast.error(intl.formatMessage({ id: "Couldn't copy to clipboard" }))
    }
  }

  // §6 closes `role` to 'user' | 'assistant'. Everything that is not a user
  // message renders as the assistant, so an unmodelled role degrades instead of
  // crashing — but nothing here is built for a third role.
  if (message.role === 'user') {
    return (
      <div className='lum-row lum-row-me'>
        {/* Direction from the text itself — see the note in `AnswerBubble`. */}
        <div className='lum-bubble-user' dir='auto'>
          {message.content}
        </div>
        <div className='lum-uav'>
          <User size={15} aria-hidden='true' />
        </div>
      </div>
    )
  }

  const hasText = message.content.trim().length > 0
  const showTrace = !!trace && !isEmptyTrace(trace)

  /**
   * A message with no text and nothing to show for itself renders NOTHING.
   *
   * The service persists an empty assistant message for a turn that produced
   * only reasoning, and this path used to render it as a fully chromed bubble —
   * avatar, name, empty markdown body, Copy button. The live path has always
   * guarded on empty text; only the persisted one did not. Messages already
   * stored in that state render blank until this guard exists.
   *
   * An empty message carrying an approval block is not handled here: the card is
   * a SIBLING of this component in `ChatInner`, so skipping the bubble does not
   * skip the card — which is the whole reason the card must not live inside it.
   */
  if (!hasText && !showTrace) return null

  return (
    <AssistantRow>
      {showTrace && trace ? (
        <TurnActivity trace={trace} isLive={false} hasAnswer={hasText} />
      ) : null}
      {hasText ? (
        <>
          <AnswerBubble content={message.content} />
          <button type='button' className='lum-copy' onClick={handleCopy}>
            <Copy size={13} aria-hidden='true' /> {intl.formatMessage({ id: 'Copy' })}
          </button>
        </>
      ) : null}
    </AssistantRow>
  )
}

export default MessageBubble
